import { motion, useInView } from "framer-motion";
import { Target, Award } from "lucide-react";
import { useRef } from "react";
import { CarouselComponent } from "../components/Carousel";

const Highlight = ({ children }) => (
  <span className="text-primary font-semibold relative">
    {children}
    <motion.span
      className="absolute bottom-0 left-0 w-full h-[2px] bg-primary"
      initial={{ scaleX: 0 }}
      animate={{ scaleX: 1 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    />
  </span>
);

const teamMembers = [
  {
    name: "John Smith",
    role: "Founder & CEO",
    image:
      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
  },
  {
    name: "Sarah Johnson",
    role: "Head Technician",
    image:
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
  },
  {
    name: "Michael Chen",
    role: "Camera Specialist",
    image:
      "https://images.unsplash.com/photo-1519244703995-f4e0f30006d5?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
  },
];

const About = () => {
  const storyRef = useRef(null);
  const missionRef = useRef(null);

  const isStoryInView = useInView(storyRef, { once: true, amount: 0.3 });
  const isMissionInView = useInView(missionRef, { once: true, amount: 0.3 });

  return (
    <div className="container py-20">
      {/* Company Story Section */}
      <section className="mb-20" ref={storyRef}>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isStoryInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-4xl mx-auto text-center"
        >
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={
              isStoryInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-4xl font-bold mb-6"
          >
            Our Story
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={
              isStoryInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-lg text-muted-foreground mb-8"
          >
            Established in <Highlight>1984</Highlight> in Chennai by{" "}
            <Highlight>Mr. Kamalesh Kumar</Highlight>, Camera Service Centre has
            been a trusted name in the world of camera servicing for over{" "}
            <Highlight>four decades</Highlight>. With a passion for precision
            and a commitment to excellence, we've become pioneers in
            professional camera care across <Highlight>South India</Highlight>.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={
              isStoryInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-lg text-muted-foreground mb-8"
          >
            Our journey with <Highlight>Nikon</Highlight> began in{" "}
            <Highlight>1996</Highlight>, when our proposal to Nikon Singapore
            Pvt. Ltd. led to a personal visit by their top officials—Managing
            Director <Highlight>Mr. Satoshi Yagi San</Highlight> and Service
            Manager <Highlight>Mr. Hajime Nijjima San</Highlight>. Impressed by
            Mr. Kamalesh's skills, he was invited for training in{" "}
            <Highlight>1997</Highlight> under Mr. Ishikawa. Following this, we
            were officially authorised in <Highlight>1998</Highlight> as Nikon's
            only authorised service centre for South India. The launch was
            celebrated in the presence of film celebrities, press, industry
            leaders, and notable institutions.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={
              isStoryInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.5, delay: 0.5 }}
            className="text-lg text-muted-foreground mb-8"
          >
            In <Highlight>2001</Highlight>, we expanded into{" "}
            <Highlight>Canon</Highlight> services, acquiring advanced testing
            and calibration equipment. Mr. Kamalesh received formal training,
            and in <Highlight>2002</Highlight>, we were recognised as Canon's
            exclusive authorised service centre for South India. This made us
            the <Highlight>only centre in Asia</Highlight> authorised by both
            Nikon and Canon—a distinction we are proud of.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={
              isStoryInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.5, delay: 0.6 }}
            className="text-lg text-muted-foreground mb-8"
          >
            Our clientele includes <Highlight>ISRO</Highlight>,{" "}
            <Highlight>The Hindu</Highlight>, and photography professionals from{" "}
            <Highlight>Tamil Nadu</Highlight>,{" "}
            <Highlight>Andhra Pradesh</Highlight>, <Highlight>Kerala</Highlight>
            , and <Highlight>Karnataka</Highlight>. We've gained a reputation
            for offering <Highlight>same-day service</Highlight>, reliability,
            and technical excellence. As a token of gratitude, we conducted free
            service camps across South India, benefiting thousands of
            photographers.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={
              isStoryInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.5, delay: 0.7 }}
            className="text-lg text-muted-foreground mb-8"
          >
            Canon further entrusted us with servicing{" "}
            <Highlight>XL1 video camcorders</Highlight> used by{" "}
            <Highlight>BCCI</Highlight>, expanding our reach nationally,
            including cities like <Highlight>Delhi</Highlight>,{" "}
            <Highlight>Mumbai</Highlight>, and <Highlight>Kolkata</Highlight>.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={
              isStoryInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.5, delay: 0.8 }}
            className="text-lg text-muted-foreground"
          >
            Today, the Camera Service Centre name is known and respected across
            India. Our backbone is our{" "}
            <Highlight>technically strong and dedicated team</Highlight>,
            trained to deliver industry-standard service with{" "}
            <Highlight>speed</Highlight>, <Highlight>quality</Highlight>, and{" "}
            <Highlight>care</Highlight>.
          </motion.p>
        </motion.div>
      </section>

      {/* Mission Section */}
      <section className="mb-20" ref={missionRef}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={
            isMissionInView
              ? { opacity: 1, scale: 1 }
              : { opacity: 0, scale: 0.9 }
          }
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="flex flex-col items-center text-center p-8 bg-muted/50 rounded-lg max-w-3xl mx-auto"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={
              isMissionInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Target className="h-12 w-12 text-primary mb-6" />
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={
              isMissionInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-2xl font-bold mb-4"
          >
            Our Mission
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={
              isMissionInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-muted-foreground"
          >
            To delight every customer with <Highlight>fast</Highlight>,{" "}
            <Highlight>reliable</Highlight>, and <Highlight>genuine</Highlight>{" "}
            service.
          </motion.p>
        </motion.div>
      </section>

      <CarouselComponent />

      {/* Team Section - Commented out
      <section>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl font-bold mb-4">Meet Our Team</h2>
          <p className="text-muted-foreground">
            Our experienced team of camera specialists is dedicated to providing
            the best service possible.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {teamMembers.map((member, index) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="flex flex-col items-center text-center"
            >
              <div className="relative w-48 h-48 mb-6">
                <img
                  src={member.image}
                  alt={member.name}
                  className="rounded-full object-cover w-full h-full"
                />
                <div className="absolute inset-0 rounded-full bg-primary/10 animate-pulse" />
              </div>
              <h3 className="text-xl font-bold mb-2">{member.name}</h3>
              <p className="text-muted-foreground">{member.role}</p>
            </motion.div>
          ))}
        </div>
      </section>
      */}
    </div>
  );
};

export default About;
